/**
 * UAVTradeZone - Notification Interface
 * Date: 2026-02-09
 *
 * TypeScript interfaces for the notifications feature.
 */

// Notification types from server
export type NotificationType =
  | "broadcast-notification"
  | "broadcast-response"
  | "inventory-upload-complete"
  | "inventory-export-complete"
  | "inventory-confirmation-reminder"
  | "inventory-removed"
  | "login-confirmation"
  | "reset-password";

// Category for UI display (grouped types)
export type NotificationCategory =
  | "broadcast"
  | "inventory"
  | "security";

// Single notification item
export interface Notification {
  _id: string;
  type: NotificationType;
  subject: string | null;
  payload: NotificationPayload;
  status: "queued" | "processing" | "sent" | "failed";
  createdAt: string;
  dismissedAt: string | null;
}

// Payload varies by notification type
export interface NotificationPayload {
  // Common fields
  userDisplayName?: string;
  supportUrl?: string;

  // Broadcast notification fields
  BROADCAST_TYPE?: string;
  ITEM_TITLE?: string;
  MANUFACTURER?: string;
  MODEL?: string;
  PART_NUMBER?: string;
  SENDER_NAME?: string;
  COMPANY_NAME?: string;
  RESPONSE_LINK?: string;

  // Broadcast response fields
  BROADCAST_TITLE?: string;
  RESPONDER_NAME?: string;
  RESPONDER_COMPANY?: string;
  RESPONDER_MESSAGE?: string;

  // Inventory upload fields
  fileName?: string;
  stats?: {
    totalRows?: number;
    successRows?: number;
    failedRows?: number;
  };

  // Inventory export fields
  itemCount?: number;
  exportDate?: string;

  // Inventory confirmation fields
  daysRemaining?: number;
  inventoryUrl?: string;

  // Security fields
  loginTime?: string;
  resetUrl?: string;

  // Generic catch-all for other fields
  [key: string]: unknown;
}

// API response for notifications list
export interface NotificationsResponse {
  success: boolean;
  data: {
    notifications: Notification[];
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
  };
}

// API response for notification count
export interface NotificationCountResponse {
  success: boolean;
  data: {
    count: number;
  };
}

// Utility function to get category from notification type
export function getNotificationCategory(type: NotificationType): NotificationCategory {
  switch (type) {
    case "broadcast-notification":
    case "broadcast-response":
      return "broadcast";
    case "inventory-upload-complete":
    case "inventory-export-complete":
    case "inventory-confirmation-reminder":
    case "inventory-removed":
      return "inventory";
    case "login-confirmation":
    case "reset-password":
      return "security";
    default:
      return "broadcast";
  }
}

// Utility function to get icon for notification type
export function getNotificationIcon(type: NotificationType): string {
  switch (type) {
    case "broadcast-notification":
      return "/assets/broadcast.svg";
    case "broadcast-response":
      return "/assets/envelop.svg";
    case "inventory-upload-complete":
      return "/assets/upload.svg";
    case "inventory-export-complete":
      return "/assets/inventory.svg";
    case "inventory-confirmation-reminder":
    case "inventory-removed":
      return "/assets/inventory.svg";
    case "login-confirmation":
    case "reset-password":
      return "/assets/setting.svg";
    default:
      return "/assets/notifications.svg";
  }
}

// Utility function to get display title for notification
export function getNotificationTitle(notification: Notification): string {
  const { type, payload } = notification;

  switch (type) {
    case "broadcast-notification":
      return payload.ITEM_TITLE || payload.BROADCAST_TITLE || "New Broadcast";
    case "broadcast-response":
      return `Response from ${payload.RESPONDER_NAME || "User"}`;
    case "inventory-upload-complete":
      return payload.fileName
        ? `Inventory Upload: ${payload.fileName}`
        : "Inventory Upload Complete";
    case "inventory-export-complete":
      return payload.fileName
        ? `Inventory Export: ${payload.fileName}`
        : "Inventory Export Complete";
    case "inventory-confirmation-reminder":
      return `Confirm Your Inventory (${payload.daysRemaining || 0} days remaining)`;
    case "inventory-removed":
      return "Inventory Removed";
    case "login-confirmation":
      return "New Login Detected";
    case "reset-password":
      return "Password Reset Requested";
    default:
      return "Notification";
  }
}

// Utility function to get description for notification
export function getNotificationDescription(notification: Notification): string {
  const { type, payload } = notification;

  switch (type) {
    case "broadcast-notification":
      return `${payload.BROADCAST_TYPE || "WTS"} from ${payload.COMPANY_NAME || payload.SENDER_NAME || "Unknown"}`;
    case "broadcast-response":
      return payload.RESPONDER_MESSAGE
        ? payload.RESPONDER_MESSAGE.slice(0, 100)
        : `${payload.RESPONDER_COMPANY || "A company"} responded to your broadcast`;
    case "inventory-upload-complete": {
      const stats = payload.stats;
      if (stats) {
        return `${stats.successRows || 0} items added${stats.failedRows ? `, ${stats.failedRows} failed` : ""}`;
      }
      return "Your inventory upload has been processed";
    }
    case "inventory-export-complete":
      return payload.itemCount
        ? `${payload.itemCount} items exported - ready for download`
        : "Your inventory export is ready for download";
    case "inventory-confirmation-reminder":
      return "Please confirm your inventory is still active to keep it visible";
    case "inventory-removed":
      return "Your inventory was removed due to inactivity";
    case "login-confirmation":
      return payload.loginTime ? `Login at ${payload.loginTime}` : "A new login was detected on your account";
    case "reset-password":
      return "A password reset was requested for your account";
    default:
      return "";
  }
}

// Utility function to get action URL for notification
export function getNotificationActionUrl(notification: Notification): string | null {
  const { type, payload } = notification;

  switch (type) {
    case "broadcast-notification":
      return payload.RESPONSE_LINK || null;
    case "broadcast-response":
      return "/broadcasts";
    case "inventory-upload-complete":
    case "inventory-confirmation-reminder":
    case "inventory-removed":
      return payload.inventoryUrl || "/inventory";
    case "inventory-export-complete":
      return "/inventory";
    default:
      return null;
  }
}

// Utility function to get action button text
export function getNotificationActionText(type: NotificationType): string | null {
  switch (type) {
    case "broadcast-notification":
      return "View Broadcast";
    case "broadcast-response":
      return "View Broadcasts";
    case "inventory-upload-complete":
    case "inventory-confirmation-reminder":
    case "inventory-removed":
      return "View Inventory";
    case "inventory-export-complete":
      return "Download Export";
    default:
      return null;
  }
}
